// Which kinds of transport a search, a planner or a "near me" is allowed to show.
//
// PURE, over the mode registry (`src/modes.js`) and plain objects; the one
// shared SETTING and its Vue wiring is `src/use-mode-filter.js`, the chip row is
// `src/components/ModeFilter.vue`.
//
// ONE SETTING FOR THE WHOLE APP. The station sheet, the planner's From and To,
// Near me and Choose lines all ask the same question — "which modes do you want
// to see?" — and they all read the same answer, so turning trams off in one
// turns them off in every other.
//
// NOTHING HERE NAMES A MODE. A chip is built from a registry declaration: its
// words are `labels`, its colour is `badge.colour`. A bus mode declared in
// `src/modes.js` gets a chip, hidden-row copy and a place in a mixed near-me
// list without a line of this file changing.

import { modesOf } from './modes.js';
import { modeOf } from './stop-search.js';

export const TRAIN = 'train';

/** Trains' own words, in the shape a mode's `labels` has. */
const TRAIN_WORDS = Object.freeze({
  one: 'train', many: 'trains', stop: 'station', stops: 'stations', line: 'line', lines: 'lines', tag: 'TRAIN',
});

const upper = (word) => `${String(word).charAt(0).toUpperCase()}${String(word).slice(1)}`;

/**
 * The chips a city offers, in order: trains, then each mode with stops of its
 * own. EMPTY for a city with trains only — one chip is not a filter.
 *
 * @param {string} city
 * @param {object} [o]
 * @param {string[]|null} [o.only] keep only these ids (a screen that can show no more than that)
 */
export function filterChips(city, { only = null } = {}) {
  const adapters = modesOf(city).filter((m) => m.source === 'adapter');
  if (!adapters.length) return [];
  const all = [
    { id: TRAIN, label: 'Trains', tag: TRAIN_WORDS.tag, colour: null, words: TRAIN_WORDS, mode: null },
    ...adapters.map((m) => ({
      id: m.id, label: upper(m.labels.many), tag: m.labels.tag, colour: m.badge?.colour ?? null, words: m.labels, mode: m,
    })),
  ];
  const chips = only ? all.filter((c) => only.includes(c.id)) : all;
  return chips.length > 1 ? chips : [];
}

/** The storage key of one explicit choice: city-scoped, so two cities never share a switch. */
export const choiceKey = (city, id) => `${city}:${id}`;

/**
 * Which chips are on: what the person chose, else the default.
 *
 * DEFAULTS: trains on. A mode is on when the phone has opted in to it (which is
 * exactly when it watches lines there — see `src/mode-state.js`), otherwise
 * off, and a chip for a mode the phone has not opted in to is off, always; it is still a chip, so one tap brings it in. At least one chip is always
 * on — a search that can show nothing is a broken screen — so when nothing
 * resolves on, the first chip is.
 *
 * @param {Array} chips
 * @param {object} stored   `{ 'melbourne:tram': false, … }`, explicit choices only
 * @param {string} city
 * @param {(mode: object) => boolean} modeIsOn
 * @returns {Record<string, boolean>}
 */
export function resolveOn(chips, stored, city, modeIsOn) {
  const out = {};
  for (const c of chips) {
    const choice = stored?.[choiceKey(city, c.id)];
    out[c.id] = typeof choice === 'boolean' ? choice : (c.mode ? Boolean(modeIsOn(c.mode)) : true);
  }
  if (chips.length && !chips.some((c) => out[c.id])) out[chips[0].id] = true;
  return out;
}

/**
 * The chips in the person's order. `stored` is the ids they put in order for
 * this city (`['bus', 'train', …]`), possibly out of date: an id the city no
 * longer has is dropped and a chip that is not in it follows, in the registry's
 * own order. Nothing stored is the registry's order: trains, then each mode as
 * it is declared.
 */
export function orderChips(chips, stored) {
  const known = new Map(chips.map((c) => [c.id, c]));
  const seen = new Set();
  const out = [];
  for (const id of Array.isArray(stored) ? stored : []) {
    if (known.has(id) && !seen.has(id)) { seen.add(id); out.push(known.get(id)); }
  }
  for (const c of chips) if (!seen.has(c.id)) out.push(c);
  return out;
}

/** The ids with the one at `from` moved to `to`. Out-of-range moves return the same list. */
export function moveId(ids, from, to) {
  const n = ids.length;
  if (!(from >= 0 && from < n && to >= 0 && to < n) || from === to) return ids;
  const next = [...ids];
  const [one] = next.splice(from, 1);
  next.splice(to, 0, one);
  return next;
}

/**
 * The opening priority: the ids that are on, in the person's order. A mode is
 * never ranked while it is off, whatever its place in the list.
 */
export const priorityIds = (ordered, on) => ordered.filter((c) => on[c.id]).map((c) => c.id);

/**
 * What a chip MEANS for the whole app (Settings > Transport, the board's chips):
 * on only if the filter says so AND, for a mode the person opts in to, the phone
 * has it. `resolveOn` stays the screens' own filter, where a choice stands alone.
 * At least one stays on.
 */
export function effectiveOn(chips, on, modeIsOn) {
  const out = {};
  for (const c of chips) out[c.id] = Boolean(on[c.id]) && (!c.mode || Boolean(modeIsOn(c.mode)));
  if (chips.length && !chips.some((c) => out[c.id])) out[chips[0].id] = true;
  return out;
}

/** Flip one chip, unless it is the last one on. Returns the same object when refused. */
export function flipped(on, id) {
  const next = { ...on, [id]: !on[id] };
  return Object.values(next).some(Boolean) ? next : on;
}

/** The ids that are on, in chip order. */
export const activeIds = (chips, on) => chips.filter((c) => on[c.id]).map((c) => c.id);

/**
 * Split rows by mode: what shows, and how many of each off mode were held back.
 * A row's mode is the one `src/stop-search.js` reads (`mode`, else a station).
 * With no chips (a trains-only city) nothing is ever hidden.
 */
export function partitionRows(rows, chips, on, modeIdOf = modeOf) {
  if (!chips.length) return { shown: [...(rows ?? [])], hidden: {} };
  const shown = [];
  const hidden = {};
  for (const row of rows ?? []) {
    const id = modeIdOf(row);
    if (on[id] === false) hidden[id] = (hidden[id] ?? 0) + 1;
    else shown.push(row);
  }
  return { shown, hidden };
}

/**
 * The one-line notices for what was hidden, so nothing vanishes silently:
 * `{ id, count, text: '3 tram stops hidden', action: 'Show trams' }`, in chip
 * order. `kind` is the noun: 'stop' (a station or a mode's stop) or 'line'.
 */
export function hiddenNotes(chips, hidden, kind = 'stop') {
  return chips.filter((c) => hidden?.[c.id] > 0).map((c) => {
    const count = hidden[c.id];
    const one = kind === 'line' ? c.words.line : c.words.stop;
    const many = kind === 'line' ? c.words.lines : c.words.stops;
    const noun = c.id === TRAIN ? (count === 1 ? one : many) : `${c.words.one} ${count === 1 ? one : many}`;
    return { id: c.id, count, text: `${count} ${noun} hidden`, action: `Show ${c.words.many}` };
  });
}

/**
 * Stations and the stops of any number of modes, as ONE list, nearest first.
 * Ties keep the order given (stations, then modes in the order passed), so the
 * result is the same on every run.
 *
 * @param {Array} stations  `[{ station, metres }]`
 * @param {Record<string, Array>} stopsByMode `{ [modeId]: [{ station: row, metres }] }`
 */
export function mixedNear(stations, stopsByMode = {}, { limit = 6 } = {}) {
  const all = [
    ...(stations ?? []),
    ...Object.values(stopsByMode).flatMap((list) => list ?? []),
  ].map((entry, i) => ({ entry, i }));
  return all
    .filter(({ entry }) => entry?.station && entry.metres != null && Number.isFinite(Number(entry.metres)))
    .sort((a, b) => a.entry.metres - b.entry.metres || a.i - b.i)
    .slice(0, Math.max(0, limit))
    .map(({ entry }) => entry);
}
